import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    MenuItem,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    Select,
    Alert,
} from "@mui/material";

import { generateEmail, GetallEmails } from "../../utils/templatesApi";
import { toast } from "react-toastify";

const GenerateEmail = ({ userId, setHeading = () => { } }) => {
    const [prospectName, setProspectName] = useState("");
    const [jobPosting, setJobPosting] = useState("");
    const [email, setEmail] = useState("");
    const [mailboxes, setMailboxes] = useState([]);
    const [selectedMailbox, setSelectedMailbox] = useState("");
    const [model, setModel] = useState("gemini-2.5-lite");

    const [generatedEmail, setGeneratedEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [loading, setLoading] = useState(false);

    /* ================= LOAD MAILBOXES ================= */
    useEffect(() => {
        setHeading("AI Email Generator");

        const fetchMailboxes = async () => {
            try {
                const data = await GetallEmails();
                setMailboxes(data || []);
            } catch (error) {
                console.error(error);
                toast.error("Failed to load email accounts");
            }
        };

        fetchMailboxes();
    }, [setHeading]);

    /* ================= EXTRACT SUBJECT ================= */
    const extractSubject = (text) => {
        if (!text) return { subject: "", body: "" };

        const match = text.match(/^Subject:\s*(.*)/i);

        if (match) {
            return {
                subject: match[1],
                body: text.replace(/^Subject:.*\n?/i, "").trim(),
            };
        }

        return { subject: "", body: text };
    };

    /* ================= GENERATE EMAIL ================= */
    const handleGenerate = async () => {
        if (!selectedMailbox) {
            toast.error("Please select an email account");
            return;
        }

        if (!prospectName.trim() || !jobPosting.trim()) {
            toast.error("Please fill all required fields");
            return;
        }

        try {
            setLoading(true);
            setGeneratedEmail("");
            setSubject("");

            const response = await generateEmail({
                email: selectedMailbox,
                prospect_name: prospectName,
                job_posting: jobPosting,
                model,
                email: selectedMailbox,
            });

            const aiText = response?.generated_solution || "";

            const { subject, body } = extractSubject(aiText);

            setSubject(subject);
            setGeneratedEmail(body);

            toast.success("Email generated successfully!");
        } catch (error) {
            console.error(error);
            toast.error("Failed to generate email");
        } finally {
            setLoading(false);
        }
    };

    /* ================= COPY EMAIL ================= */
    const handleCopy = async () => {
        if (!generatedEmail) return;

        const fullEmail = subject
            ? `Subject: ${subject}\n\n${generatedEmail}`
            : generatedEmail;

        try {
            await navigator.clipboard.writeText(fullEmail);
            toast.success("Copied to clipboard!");
        } catch (err) {
            toast.error("Copy failed");
        }
    };

    /* ================= UI ================= */
    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundColor: "#f5f7fa",
                p: { xs: 2, md: 5 },
                width: "100%",
                paddingTop: 0
                // display: "flex",
                // flexDirection: "column",
                // justifyContent: "center",
                // alignItems: "center",
            }}
        >
            {/* PAGE HEADER */}
            <Box mb={4} sx={{ textAlign: "center" }}>
                <Typography variant="h4" fontWeight={700} >
                    AI Email Generator
                </Typography>
                <Typography color="text.secondary" mt={1}>
                    Create personalized outreach emails powered by AI.
                </Typography>
            </Box>

            <Grid container spacing={4} display="flex" flexDirection="column">
                {/* ================= LEFT PANEL ================= */}
                <Grid item xs={12} md={5} >
                    <Card
                        sx={{
                            borderRadius: 4,
                            boxShadow: "0 8px 32px rgba(0,0,0,0.06)",
                        }}
                    >
                        <CardContent sx={{ p: 5 }} >
                            <Typography variant="h5" fontWeight={700} mb={1}>
                                Create Email
                            </Typography>

                            <Typography color="text.secondary" mb={4}>
                                Fill the details below to generate a personalized outreach email.
                            </Typography>

                            <Grid container spacing={3} display="flex" flexDirection="column">
                                {/* Email Account */}
                                <Grid item xs={12} >
                                    <FormControl fullWidth>
                                        <InputLabel>Email Account</InputLabel>
                                        <Select
                                            value={selectedMailbox}
                                            label="Email Account"
                                            onChange={(e) => setSelectedMailbox(e.target.value)}
                                        >
                                            {mailboxes.map((item) => (
                                                <MenuItem key={item.email} value={item.email}>
                                                    {item.email}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Prospect + Model in one row */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Prospect Name"
                                        value={prospectName}
                                        onChange={(e) => setProspectName(e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="AI Model"
                                        value={model}
                                        onChange={(e) => setModel(e.target.value)}
                                    >

                                        <MenuItem value="gpt-4o-mini">
                                            Gpt-4o-mini
                                        </MenuItem>
                                        <MenuItem value="gemini-2.5-lite">
                                            Gemini 2.5 Lite
                                        </MenuItem>
                                        <MenuItem value="gemini-2.5-flash">
                                            Gemini 2.5 Flash (Fast)
                                        </MenuItem>
                                        <MenuItem value="gemini-1.5-pro">
                                            Gemini 1.5 Pro
                                        </MenuItem>
                                        <MenuItem value="gemini-2.5-pro">
                                            Gemini 2.5 Pro
                                        </MenuItem>
                                    </TextField>
                                </Grid>

                                {/* Job Posting Full Width */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={8}
                                        label="Job Posting"
                                        value={jobPosting}
                                        onChange={(e) => setJobPosting(e.target.value)}
                                    />
                                </Grid>

                                {/* Button */}
                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        onClick={handleGenerate}
                                        disabled={loading}
                                        sx={{
                                            height: 55,
                                            borderRadius: 3,
                                            fontWeight: 600,
                                            textTransform: "none",
                                            mt: 2
                                        }}
                                    >
                                        {loading ? (
                                            <CircularProgress size={22} color="inherit" />
                                        ) : (
                                            "Generate Email"
                                        )}
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* ================= RIGHT PANEL ================= */}
                <Grid item xs={12} md={7}>
                    <Card
                        sx={{
                            borderRadius: 3,
                            boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <CardContent sx={{ p: 4, flexGrow: 1 }}>
                            <Typography variant="h6" fontWeight={600} mb={3}>
                                Email Preview
                            </Typography>

                            {!generatedEmail && !loading && (


                                <Alert severity="info">
                                    Generated email will appear here.
                                </Alert>
                            )}

                            {loading && (
                                <Box textAlign="center" mt={4}>
                                    <CircularProgress />
                                    <Typography mt={2} color="text.secondary">
                                        Generating email...
                                    </Typography>
                                </Box>
                            )}

                            {generatedEmail && (
                                <>
                                    {subject && (
                                        <>
                                            <Typography
                                                variant="subtitle2"
                                                color="text.secondary"
                                            >
                                                Subject
                                            </Typography>
                                            <Typography fontWeight={600} mb={2}>
                                                {subject}
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />
                                        </>
                                    )}

                                    <Typography
                                        sx={{
                                            whiteSpace: "pre-wrap",
                                            lineHeight: 1.7,
                                            fontSize: 14,
                                        }}
                                    >
                                        {generatedEmail}
                                    </Typography>
                                </>
                            )}
                        </CardContent>

                        {generatedEmail && (
                            <Box p={3}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={handleCopy}
                                    sx={{ textTransform: "none", fontWeight: 600 }}
                                >
                                    Copy to Clipboard
                                </Button>
                            </Box>
                        )}
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default GenerateEmail;